import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FaArrowRight } from "react-icons/fa";
import {
    Brain,
    Gauge,
    Target,
    Shield,
    BarChart3,
    Clock3,
    ScanSearch,
    Expand,
    Coins,
    Cpu,
    Rocket,
    ClipboardCheck,
    Bot,
    TrendingUp,
    CircleAlert,
    Monitor,
    Check,
    X,
} from "lucide-react";

const ApiServices = () => {
    const navigate = useNavigate();
    const [plans, setPlans] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const comparison = [
        {
            aspect: "Execution",
            manual: "Trades are executed manually by monitoring the market.",
            algo: "Trades are executed automatically based on predefined rules.",
            manualIcon: Monitor,
            algoIcon: Cpu,
        },
        {
            aspect: "Speed",
            manual: "Slower due to human reaction time.",
            algo: "Ultra-fast execution in milliseconds.",
            manualIcon: Gauge,
            algoIcon: Rocket,
        },
        {
            aspect: "Emotion",
            manual: "Decisions are influenced by fear and greed.",
            algo: "Completely rule-based execution with no emotions.",
            manualIcon: Brain,
            algoIcon: ClipboardCheck,
        },
        {
            aspect: "Discipline",
            manual: "Difficult to maintain consistent discipline.",
            algo: "Every trade follows predefined rules.",
            manualIcon: Target,
            algoIcon: Bot,
        },
        {
            aspect: "Consistency",
            manual: "Results vary from trade to trade.",
            algo: "Delivers consistent execution.",
            manualIcon: TrendingUp,
            algoIcon: TrendingUp,
        },
        {
            aspect: "Risk Control",
            manual: "Depends on individual judgment.",
            algo: "Uses predefined risk parameters.",
            manualIcon: CircleAlert,
            algoIcon: Shield,
        },
        {
            aspect: "Backtesting",
            manual: "Limited ability to test strategies.",
            algo: "Extensively tested using historical data.",
            manualIcon: ScanSearch,
            algoIcon: BarChart3,
        },
        {
            aspect: "24/7 Monitoring",
            manual: "Requires constant screen time.",
            algo: "Automatically monitors markets.",
            manualIcon: Clock3,
            algoIcon: Bot,
        },
        {
            aspect: "Scalability",
            manual: "Limited by human capacity.",
            algo: "Handles multiple strategies simultaneously.",
            manualIcon: Expand,
            algoIcon: Expand,
        },
        {
            aspect: "Execution Cost",
            manual: "Higher due to slippage and errors.",
            algo: "Lower through automation.",
            manualIcon: Coins,
            algoIcon: Coins,
        },
    ];

    useEffect(() => {
        const fetchPlans = async () => {
            try {
                const response = await fetch("/APIServicesPlan.json");
                if (!response.ok) {
                    throw new Error("Failed to fetch plans");
                }
                const jsonData = await response.json();
                setPlans(jsonData);
            } catch (error) {
                console.error("Error fetching data:", error);
                setError(error.message);
            } finally {
                setIsLoading(false);
            }
        };

        fetchPlans();
    }, []);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-slate-50">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex justify-center items-center min-h-screen bg-slate-50">
                <div className="bg-white p-8 rounded-3xl shadow-xl text-center max-w-md border border-slate-200">
                    <h2 className="text-2xl font-bold text-rose-500 mb-4">Error Loading Plans</h2>
                    <p className="text-slate-600 mb-6">{error}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 px-8 rounded-xl transition duration-300 shadow-md shadow-emerald-600/20"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <>

            <div className="bg-slate-50 text-slate-800 antialiased selection:bg-emerald-500 selection:text-white">
                <style>{`
                @keyframes ticker {
                    0% { transform: translateX(0); }
                    100% { transform: translateX(-50%); }
                }
                .animate-ticker {
                    display: flex;
                    width: max-content;
                    animation: ticker 25s linear infinite;
                }
                .animate-ticker:hover {
                    animation-play-state: paused;
                }
                @keyframes float {
                    0%, 100% { transform: translateY(0px) rotate(0deg); }
                    50% { transform: translateY(-8px) rotate(1deg); }
                }
                .animate-float {
                    animation: float 6s ease-in-out infinite;
                }
                .perspective-1000 {
                    perspective: 1000px;
                }
                .preserve-3d {
                    transform-style: preserve-3d;
                    transition: transform 0.5s cubic-bezier(0.23, 1, 0.32, 1), box-shadow 0.5s ease;
                }
                .preserve-3d:hover {
                    transform: translateY(-8px) rotateX(3deg) rotateY(-2deg);
                }
                .gradient-text-animated {
                    background: linear-gradient(135deg, #059669 0%, #10B981 50%, #D97706 100%);
                    background-size: 200% auto;
                    color: transparent;
                    -webkit-background-clip: text;
                    animation: shine 4s linear infinite;
                }
                @keyframes shine {
                    to { background-position: 200% center; }
                }
               
                .datacontainer{
                    z-index:2;
                    position: relative;
                }
                .section1{
                position:relative;
                }
                .tick {
                    position: absolute;
                    right: -60px;
                    bottom: -132px;
                }
                .section3{
                position:relative;
                }
                .tick2 {
                    position: absolute;
                    left: -80px;
                    bottom: -132px;
                }

            `}</style>



                {/* NEW TOP HEADING SECTION */}
                <section className="py-12 bg-white border-b border-slate-200 text-center">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                            NextEra Research API Bridge Service
                        </h2>
                        <p className="text-slate-600 mt-3 text-base sm:text-lg font-medium">
                            Seamless integration for your trading and research needs
                        </p>
                    </div>
                </section>
                {/* 3. SECTION 1: ABOUT API BRIDGE + ENHANCED 3D RADIAL DIAGRAM */}
                <section id="services" className="py-16 lg:py-20 bg-gradient-to-b from-emerald-50/50 via-white to-slate-50 relative overflow-hidden">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                        <div className="grid lg:grid-cols-12 gap-12 items-center">
                            <div className="lg:col-span-6 space-y-6">
                                <span className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-xs font-bold uppercase tracking-wider border border-emerald-200">
                                    Automated Integration
                                </span>
                                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 leading-tight">
                                    About Our <span className="gradient-text-animated">API Bridge Service</span>
                                </h1>
                                <p className="text-slate-600 leading-relaxed text-sm sm:text-base font-normal">
                                    In today's fast-paced financial markets, seamless communication between trading platforms and research tools is essential. Our API Bridge service provides this critical connection, enabling real-time data exchange between your systems and our research analytics.                            </p>
                                <p className="text-slate-600 leading-relaxed text-sm sm:text-base font-normal">
                                    As a dedicated research analytics, we offer this integration capability across all our services - Options, Cash, Futures, and MCX. The API Bridge gives you flexible configuration options with both manual and automatic modes to suit your trading style.                            </p>
                                <p className="text-slate-600 leading-relaxed text-sm sm:text-base font-normal">
                                    Customize your integration with position sizing based on your investment profile and other parameters. Please note this is a delivery mechanism only - all trading calls originate from our research analysts based on market conditions.                            </p>
                            </div>

                            {/* Interactive 3D Radial Diagram */}
                            <div className="lg:col-span-6 perspective-1000">
                                <div className="preserve-3d bg-white/90 border border-slate-200/90 backdrop-blur-xl p-8 rounded-3xl shadow-2xl relative flex flex-col items-center justify-center min-h-[460px]">
                                    <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-emerald-500 via-teal-500 to-amber-500 rounded-t-3xl"></div>
                                    <div className="text-xs font-extrabold uppercase tracking-widest text-emerald-700 bg-emerald-100 border border-emerald-200 px-3 py-1 rounded-full mb-8">
                                        Seamless Integration Architecture
                                    </div>
                                    <div className="relative w-72 h-72 sm:w-80 sm:h-80 flex items-center justify-center">
                                        <div className="z-20 w-28 h-28 sm:w-32 sm:h-32 bg-gradient-to-br from-purple-700 to-indigo-900 text-white rounded-full flex items-center justify-center text-center p-3 shadow-xl shadow-purple-900/30 font-extrabold text-sm sm:text-base border-4 border-white">
                                            API TRADING
                                        </div>
                                        <div className="absolute -top-4 w-24 h-24 bg-cyan-500 text-white rounded-full flex items-center justify-center text-center p-2 text-xs font-bold shadow-lg border-2 border-white hover:scale-110 transition-transform">
                                            Initiation price
                                        </div>
                                        <div className="absolute top-8 -right-4 w-24 h-24 bg-teal-500 text-white rounded-full flex items-center justify-center text-center p-2 text-xs font-bold shadow-lg border-2 border-white hover:scale-110 transition-transform">
                                            Quantity
                                        </div>
                                        <div className="absolute bottom-8 -right-4 w-24 h-24 bg-green-500 text-white rounded-full flex items-center justify-center text-center p-2 text-xs font-bold shadow-lg border-2 border-white hover:scale-110 transition-transform">
                                            Order type
                                        </div>
                                        <div className="absolute -bottom-4 w-24 h-24 bg-lime-500 text-white rounded-full flex items-center justify-center text-center p-2 text-xs font-bold shadow-lg border-2 border-white hover:scale-110 transition-transform">
                                            Profit targets
                                        </div>
                                        <div className="absolute bottom-8 -left-4 w-24 h-24 bg-yellow-400 text-white rounded-full flex items-center justify-center text-center p-2 text-xs font-bold shadow-lg border-2 border-white hover:scale-110 transition-transform">
                                            Trailing stops
                                        </div>
                                        <div className="absolute top-8 -left-4 w-24 h-24 bg-amber-500 text-white rounded-full flex items-center justify-center text-center p-2 text-xs font-bold shadow-lg border-2 border-white hover:scale-110 transition-transform">
                                            Exit price
                                        </div>
                                    </div>
                                    <p className="text-[11px] text-slate-500 mt-8 font-medium">Connect your trading platform with our research signals in minutes</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 4. SECTION 2: Why More Traders Are Choosing Algo Trading */}
                <section className="py-20 bg-gradient-to-b from-white via-slate-50 to-white border-y border-slate-200">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto mb-12">
                            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 bg-emerald-100 text-emerald-800 rounded-full text-xs font-bold uppercase tracking-wider border border-emerald-200">
                                Why Algo Trading
                            </span>
                            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-3 tracking-tight">
                                Why More Traders Are Choosing <span className="gradient-text-animated">Algo Trading</span>
                            </h2>
                            <p className="text-slate-600 mt-3 text-sm sm:text-base">See how NextEra Research's algorithmic approach outperforms traditional manual trading across every key dimension.</p>
                        </div>

                        <div className="bg-white border border-slate-200 rounded-3xl shadow-xl overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-left border-collapse">
                                    <colgroup>
                                        <col style={{ width: '19%' }} />
                                        <col style={{ width: '40.5%' }} />
                                        <col style={{ width: '40.5%' }} />
                                    </colgroup>
                                    <thead className="bg-slate-900 text-white">
                                        <tr>
                                            <th className="py-5 px-6 text-[11px] font-bold uppercase tracking-wider">
                                                Feature
                                            </th>
                                            <th className="py-5 px-6 text-[11px] font-bold uppercase tracking-wider text-rose-300 bg-slate-950/60">
                                                <div className="flex items-center justify-center gap-2">
                                                    <X size={14} strokeWidth={3} className="text-rose-400" />
                                                    <span>Traditional Manual Trading</span>
                                                </div>
                                            </th>
                                            <th className="py-5 px-6 text-[11px] font-bold uppercase tracking-wider text-emerald-300 bg-slate-950">
                                                <div className="flex items-center justify-center gap-2">
                                                    <Check size={14} strokeWidth={3} className="text-emerald-400" />
                                                    <span>NextEra Research Algo Trading</span>
                                                </div>
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-200 text-slate-700">
                                        {/* Trade Execution */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">⚡</span>
                                                    <span>Trade Execution</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Monitor size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Trades are placed manually after personal analysis.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Cpu size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Software executes trades automatically using preset rules.</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Execution Speed */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">🚀</span>
                                                    <span>Execution Speed</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Gauge size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Speed is limited by human reaction time and attention.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Rocket size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Orders execute within milliseconds for instant response.</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Emotional Decisions */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">🧠</span>
                                                    <span>Emotional Decisions</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Brain size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Fear and greed often lead to inconsistent trading choices.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <ClipboardCheck size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Decisions stay fully rule-based with zero emotional bias.</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Trading Discipline */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">✅</span>
                                                    <span>Trading Discipline</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Target size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Maintaining consistent discipline is genuinely difficult.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Bot size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Every trade follows identical predefined rules automatically.</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Consistency */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">📈</span>
                                                    <span>Consistency</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <TrendingUp size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Results vary with emotions and shifting market conditions.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <TrendingUp size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Execution stays consistent across every single trade placed.</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Risk Management */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">🛡️</span>
                                                    <span>Risk Management</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <CircleAlert size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Risk control depends on personal judgment and experience.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Shield size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Built-in stop-loss and predefined parameters protect every trade.</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Strategy Testing */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">📊</span>
                                                    <span>Strategy Testing</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <ScanSearch size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Thorough testing before risking capital is rarely possible.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <BarChart3 size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Strategies are thoroughly backtested before live deployment.</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Market Monitoring */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">⏰</span>
                                                    <span>Market Monitoring</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Clock3 size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Requires constant screen time to catch every opportunity.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Clock3 size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Markets are monitored continuously without manual supervision.</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Scalability */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">📈</span>
                                                    <span>Scalability</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Expand size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Managing multiple strategies at once quickly becomes difficult.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Expand size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Multiple strategies and markets run simultaneously with ease.</span>
                                                </div>
                                            </td>
                                        </tr>
                                        {/* Execution Efficiency */}
                                        <tr className="hover:bg-slate-50/70 transition-colors duration-200">
                                            <td className="px-6 align-middle min-h-[96px] text-[16px] font-semibold text-slate-900 leading-snug">
                                                <div className="flex items-center gap-3 py-1">
                                                    <span className="text-xl shrink-0">💰</span>
                                                    <span>Execution Efficiency</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-rose-50/30">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Coins size={20} strokeWidth={2} className="text-rose-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-medium text-rose-800">Human errors and slippage quietly raise trading costs.</span>
                                                </div>
                                            </td>
                                            <td className="px-6 align-middle min-h-[96px] bg-emerald-50/40">
                                                <div className="flex items-center gap-3 py-1">
                                                    <Coins size={20} strokeWidth={2} className="text-emerald-500 shrink-0" />
                                                    <span className="text-[14px] leading-relaxed font-semibold text-emerald-900">Precise entries and exits minimize execution inefficiencies.</span>
                                                </div>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 5. SECTION 3: Pain Points vs Gain Points 3D Cards */}
                <section className="py-20 bg-slate-100">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">Decision Analysis</span>
                            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2">Manual Trading vs Algo Trading</h2>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8">
                            <div className="perspective-1000">
                                <div className="preserve-3d bg-white border border-rose-200 p-8 rounded-3xl shadow-xl relative group h-full ">
                                    <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-rose-100 text-rose-700 text-xs font-extrabold uppercase rounded-full mb-6">
                                        ❌ Manual Trading Pain Points
                                    </div>
                                    <ul className="space-y-4 text-sm font-semibold text-slate-700">
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-extrabold text-xs">✕</span> Lack of Emotional Control</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-extrabold text-xs">✕</span> Late Entries & Early Exits</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-extrabold text-xs">✕</span> Decisions Driven by Fear & Greed</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-extrabold text-xs">✕</span> Inconsistent Trading Discipline</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-extrabold text-xs">✕</span> Stuck to the Screen All Day</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-extrabold text-xs">✕</span> No Proper Backtesting</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-extrabold text-xs">✕</span> Weak risk management</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-rose-100 text-rose-600 flex items-center justify-center font-extrabold text-xs">✕</span> Limited growth & scalability</li>
                                    </ul>
                                </div>
                            </div>

                            <div className="perspective-1000">
                                <div className="preserve-3d bg-emerald-900 text-white p-8 rounded-3xl shadow-2xl relative group h-full border border-emerald-700">
                                    <div className="inline-flex items-center gap-2 px-3.5 py-1 bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 text-xs font-extrabold uppercase rounded-full mb-6">
                                        ✓ Algo Trading Gain Points
                                    </div>
                                    <ul className="space-y-4 text-sm font-semibold text-emerald-100">
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-extrabold text-xs">✓</span> Emotion-free execution</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-extrabold text-xs">✓</span> Perfect entry & exit</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-extrabold text-xs">✓</span> 100% rule-based discipline</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-extrabold text-xs">✓</span> Back-tested & data-driven</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-extrabold text-xs">✓</span> Strong risk management</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-extrabold text-xs">✓</span> Screen-free trading</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-extrabold text-xs">✓</span> Multiple stocks, one system</li>
                                        <li className="flex items-center gap-3"><span className="w-6 h-6 rounded-full bg-emerald-500 text-slate-950 flex items-center justify-center font-extrabold text-xs">✓</span> Scalable & consistent growth</li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* 6. SECTION 4: ADVANCED RESEARCH SOLUTIONS */}
                <section className="py-20 bg-white border-y border-slate-200">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="bg-slate-50 rounded-3xl p-8 sm:p-12 border border-slate-200 shadow-xl grid lg:grid-cols-12 gap-8 items-center">
                            <div className="lg:col-span-6 space-y-4">
                                <span className="text-xs font-bold uppercase tracking-widest text-slate-900">Advanced Research Solutions</span>
                                <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-900">Powerful API Bridge Technology for Modern Trading</h2>
                                <p className="text-slate-600 text-sm leading-relaxed">
                                    An API bridge in trading is a software interface that connects different systems involved in the trading process, such as trading platforms, algorithmic trading software, and brokers. It facilitates automated trading, real-time data analysis, and efficient execution of trading signals.
                                </p>
                                <p className="text-slate-600 text-sm leading-relaxed">
                                    API bridges allow for seamless integration and communication between these systems, enabling traders to automate their strategies and execute trades with speed and accuracy.
                                </p>
                            </div>
                            <div className="lg:col-span-6 bg-white p-6 sm:p-8 rounded-2xl border border-slate-200 shadow-sm space-y-4">
                                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-900 mb-2">Key Features:</h3>
                                <div className="flex items-start gap-3">
                                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold text-xs mt-0.5 shrink-0">✓</span>
                                    <p className="text-sm text-slate-700"><strong className="text-slate-900">Connecting Systems:</strong> Acts as a bridge between trading platforms, algorithmic trading software, and brokers</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold text-xs mt-0.5 shrink-0">✓</span>
                                    <p className="text-sm text-slate-700"><strong className="text-slate-900">Automated Trading:</strong> Enables execution of research signals without manual intervention</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold text-xs mt-0.5 shrink-0">✓</span>
                                    <p className="text-sm text-slate-700"><strong className="text-slate-900">Real-time Data Analysis:</strong> Provides access to live market data for informed decision making</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold text-xs mt-0.5 shrink-0">✓</span>
                                    <p className="text-sm text-slate-700"><strong className="text-slate-900">Efficient Execution:</strong> Reduces latency and improves order placement speed</p>
                                </div>
                                <div className="flex items-start gap-3">
                                    <span className="w-5 h-5 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center font-bold text-xs mt-0.5 shrink-0">✓</span>
                                    <p className="text-sm text-slate-700"><strong className="text-slate-900">Integration:</strong> Ensures accurate and efficient data/order transmission between systems</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
                {/* 7. SECTION 5: CHOOSE YOUR API PLAN (Dynamic Data Driven) */}
                {/* CHOOSE YOUR API PLAN SECTION */}
                <section id="pricing" className="py-24 bg-slate-50">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center max-w-3xl mx-auto mb-16">
                            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 mt-2">Choose Your API Plan</h2>
                        </div>

                        <div className="grid gap-8 md:grid-cols-2 lg:grid-cols-4">
                            {plans.map((plan, index) => {
                                const isPopular = plan.popular;
                                const isPremium = plan.premium;
                                const isHighlighted = isPremium;
                                return (
                                    <div key={index} className="perspective-1000">
                                        <div className={`preserve-3d p-8 rounded-3xl shadow-xl flex flex-col justify-between items-center text-center h-full relative group border transition-all duration-300 hover:shadow-2xl ${isHighlighted
                                            ? "bg-emerald-900 text-white border-2 border-emerald-500 transform lg:-translate-y-4"
                                            : "bg-white text-slate-800 border-slate-200"
                                            }`}>
                                            {isPremium && (
                                                <div className="absolute -top-3 right-6 bg-purple-400 text-slate-950 text-[10px] font-extrabold uppercase px-3 py-1 rounded-full shadow">
                                                    {plan.premiumLabel || "Premium"}
                                                </div>
                                            )}
                                            {isPopular && !isPremium && (
                                                <div className="absolute -top-3 right-6 bg-amber-400 text-slate-950 text-[10px] font-extrabold uppercase px-3 py-1 rounded-full shadow">
                                                    {plan.popularLabel || "Popular"}
                                                </div>
                                            )}
                                            <div>
                                                <h3 className={`text-lg font-bold mb-2 ${isHighlighted ? "text-emerald-300" : "text-slate-900"}`}>
                                                    {plan.title}
                                                </h3>
                                                <div className={`text-3xl font-extrabold mb-6 ${isHighlighted ? "text-white" : "text-slate-900"}`}>
                                                    {plan.price}
                                                </div>
                                                <ul className={`space-y-3 text-xs text-left w-full ${isHighlighted ? "text-emerald-100" : "text-slate-600"}`}>
                                                    {plan.features.map((feature, i) => (
                                                        <li key={i} className="flex items-start gap-2">
                                                            <span className={`shrink-0 mt-0.5 ${isHighlighted ? "text-emerald-300" : "text-emerald-500"} font-bold`}>✓</span>
                                                            <span>{feature}</span>
                                                        </li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="mt-8 w-full">
                                                <button
                                                    onClick={() => {
                                                        const numericPrice = Number(plan.price.replace(/[₹,\s]/g, ""));
                                                        navigate("/secure-payment", {
                                                            state: {
                                                                price: numericPrice,
                                                                planTitle: plan.title,
                                                            },
                                                        });
                                                    }}
                                                    className={`w-full rounded-xl font-bold py-3 px-6 transition duration-300 shadow-md ${isHighlighted
                                                        ? "bg-emerald-500 hover:bg-emerald-400 text-slate-950 shadow-lg"
                                                        : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-emerald-600/20"
                                                        }`}
                                                >
                                                    {plan.buttonText || "Buy Now"}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
                    <div className="bg-emerald-50 rounded-3xl p-8 sm:p-12 text-center border border-emerald-100 shadow-sm">
                        <h3 className="text-2xl font-bold text-slate-900 mb-4">Need a custom solution?</h3>
                        <p className="text-slate-600 mb-8 max-w-2xl mx-auto text-sm sm:text-base">
                            Our team can create a tailored API integration to meet your specific requirements and trading volume.
                        </p>
                        <button
                            onClick={() => navigate('/contactus')}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3.5 px-8 rounded-xl transition duration-300 inline-flex items-center gap-3 shadow-lg shadow-emerald-600/20 hover:scale-105"
                        >
                            Contact Us <FaArrowRight />
                        </button>
                    </div>
                </div>
            </div>
        </>
    );
};

export default ApiServices;